import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ValidateService from "../../../service/ValidateService";
import { QueueTest } from "../../command_center/components/center/modals/console/QueuePanel";
import { showToastMessage, uuidv4 } from "../../../utils/commonFunctions";
import {
  ActiveExecutionInfo,
  EngineStatusData,
} from "../types/validationExecution.types";
import {
  calculateProgressDurations,
  extractPassFailCounts,
  filterVisibleExecutedTests,
  formatRemainingTimeMS,
  formatQueueStatusLabel,
  normalizeQueueTestStatus,
  WS_TEST_CASE_EXECUTION_ABORTED,
  WS_TEST_CASE_EXECUTION_CANCELLED,
  WS_TEST_CASE_EXECUTION_COMPLETE,
  WS_TEST_CASE_EXECUTION_IN_PROGRESS,
  WS_TESTCASE_PROGRESS_UPDATED,
  WS_TEST_PLAN_EXECUTION_ABORTED,
  WS_TEST_PLAN_EXECUTION_CANCELLED,
  WS_TEST_PLAN_EXECUTION_COMPLETE,
  WS_TEST_PLAN_EXECUTION_IN_PROGRESS,
  WS_TESTPLAN_PROGRESS_UPDATED,
  WS_VALIDATE_ABORT_EVENT,
} from "../helpers/validateWsHelpers";

export interface UseValidationExecutionParams {
  missionId?: string;
  truetwinId?: string;
  scenarioId?: string;
  socket?: WebSocket | null;
}

export interface UseValidationExecutionReturn {
  activeTest: QueueTest | null;
  executedTests: QueueTest[];
  isRunning: boolean;
  isAborting: boolean;
  abortActiveExecution: (test?: QueueTest | null) => Promise<void>;
  fetchExecutionStatus: () => Promise<void>;
  fetchSessionExecutedTests: () => Promise<void>;
  handleWebSocketMessage: (msg: any) => void;
}

const isGenericActiveName = (name?: string | null): boolean => {
  if (!name || typeof name !== "string") return true;
  const n = name.trim().toLowerCase();
  return (
    n === "" ||
    n === "active test" ||
    n === "active test plan" ||
    n === "active test case" ||
    n === "active-test" ||
    n === "active-plan" ||
    n === "unnamed test case" ||
    n === "unnamed test plan" ||
    n === "test plan undefined" ||
    n === "test case undefined" ||
    n === "test plan active-plan" ||
    n === "test case active-test"
  );
};

const getStoredTestName = (type: "TEST PLAN" | "TEST", id?: string): string | undefined => {
  try {
    const key = type === "TEST PLAN" ? "testPlanPlan" : "testTestCase";
    const raw = sessionStorage.getItem(key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    const storedId = type === "TEST PLAN" ? parsed?.value?.testPlanId : parsed?.value?.testCaseId;
    const storedName =
      (type === "TEST PLAN" ? parsed?.value?.testPlanName : parsed?.value?.testCaseName) ||
      parsed?.label ||
      parsed?.value?.name;
    if (storedName && !isGenericActiveName(storedName)) {
      if (!id || !storedId || String(id).trim().toLowerCase() === String(storedId).trim().toLowerCase()) {
        return storedName;
      }
    }
  } catch {
    // ignore
  }
  return undefined;
};

const getStoredTestId = (type: "TEST PLAN" | "TEST"): string | undefined => {
  try {
    const directKey = type === "TEST PLAN" ? "testPlanId" : "testCaseId";
    const directVal = sessionStorage.getItem(directKey);
    if (directVal && typeof directVal === "string" && directVal.trim()) {
      return directVal.trim();
    }
    const jsonKey = type === "TEST PLAN" ? "testPlanPlan" : "testTestCase";
    const raw = sessionStorage.getItem(jsonKey);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    const storedId = type === "TEST PLAN" ? parsed?.value?.testPlanId : parsed?.value?.testCaseId;
    if (storedId && typeof storedId === "string" && storedId.trim()) {
      return storedId.trim();
    }
  } catch {
    // ignore
  }
  return undefined;
};

export const useValidationExecution = ({
  missionId,
  truetwinId,
  scenarioId,
  socket,
}: UseValidationExecutionParams): UseValidationExecutionReturn => {
  const [activeTest, setActiveTest] = useState<QueueTest | null>(null);
  const [executedTests, setExecutedTests] = useState<QueueTest[]>([]);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isAborting, setIsAborting] = useState<boolean>(false);

  const activeExecutionRef = useRef<ActiveExecutionInfo | null>(null);

  const mapActiveExecutionToQueueTest = useCallback(
    (info: ActiveExecutionInfo): QueueTest => {
      const isPlan = info.kind === "TEST_PLAN";
      const total = isPlan
        ? Number(info.totalTestCases ?? info.totalEntries ?? 0)
        : Number(info.totalEntries ?? 0);
      const completed = isPlan
        ? Number(info.resolvedTestCases ?? info.completedEntries ?? 0)
        : Number(info.completedEntries ?? 0);

      const counts = extractPassFailCounts(info);
      const passed = isPlan && (info.testCasesPassed !== undefined || info.testCasesPass !== undefined)
        ? Number(info.testCasesPassed ?? info.testCasesPass)
        : counts.passed;
      const failed = isPlan && (info.testCasesFailed !== undefined || info.testCasesFail !== undefined)
        ? Number(info.testCasesFailed ?? info.testCasesFail)
        : counts.failed;

      const durationFormatted = info.estimatedDurationMS
        ? formatRemainingTimeMS(info.estimatedDurationMS)
        : "0 min";

      const rawRemaining = info.remainingTimeMS !== undefined
        ? formatRemainingTimeMS(info.remainingTimeMS)
        : undefined;

      let remainingFormatted: string;
      if (rawRemaining) {
        if (durationFormatted && durationFormatted !== "0 min") {
          remainingFormatted = `${rawRemaining} left (${durationFormatted})`;
        } else {
          remainingFormatted = `${rawRemaining} left`;
        }
      } else if (durationFormatted && durationFormatted !== "0 min") {
        remainingFormatted = `${durationFormatted} left (${durationFormatted})`;
      } else {
        remainingFormatted = durationFormatted;
      }

      const testId = isPlan
        ? info.testPlanId || info.testPlanResultId || "active-plan"
        : info.testCaseId || info.testCaseResultId || "active-test";

      const name = isPlan
        ? (info.testPlanName && !isGenericActiveName(info.testPlanName) ? info.testPlanName : "") ||
          getStoredTestName("TEST PLAN", info.testPlanId) ||
          (info.testPlanId ? `Test Plan ${info.testPlanId}` : "Test Plan")
        : (info.testCaseName && !isGenericActiveName(info.testCaseName) ? info.testCaseName : "") ||
          getStoredTestName("TEST", info.testCaseId) ||
          (info.testCaseId ? `Test Case ${info.testCaseId}` : "Test Case");

      const percentComplete =
        info.percentComplete !== undefined
          ? Number(info.percentComplete)
          : (total > 0 ? Math.round((completed / total) * 100) : 0);
      const remainingPercent =
        info.remainingPercent !== undefined
          ? Number(info.remainingPercent)
          : Math.max(0, 100 - percentComplete);

      return {
        id: testId,
        name,
        duration: durationFormatted,
        remainingDuration: remainingFormatted,
        completed,
        total,
        passed,
        failed,
        testCasesPassed: info.testCasesPassed !== undefined ? Number(info.testCasesPassed) : undefined,
        testCasesFailed: info.testCasesFailed !== undefined ? Number(info.testCasesFailed) : undefined,
        percentComplete,
        remainingPercent,
        status: "running",
        testType: isPlan ? "Test Plan" : "Test Case",
        testPlanId: info.testPlanId,
        testPlanResultId: info.testPlanResultId,
        testCaseId: info.testCaseId,
        testCaseResultId: info.testCaseResultId,
        testPlanName: info.testPlanName,
        testCaseName: info.testCaseName,
      };
    },
    [],
  );

  const fetchExecutionStatus = useCallback(async () => {
    if (!missionId || !truetwinId) return;

    try {
      const res: any =
        await ValidateService.getValidationExecutionStatus(
          missionId,
          truetwinId,
        );

      const statusData: EngineStatusData | undefined = res?.data?.data || res?.data;

      if (statusData) {
        const { isRunning: running, activeExecution } = statusData;
        const isExecutionActuallyRunning = Boolean(
          running &&
          activeExecution &&
          (activeExecution.percentComplete === undefined || Number(activeExecution.percentComplete) < 100) &&
          !["passed", "failed", "completed", "cancelled", "aborted", "manual abort"].includes(
            String(activeExecution.status || activeExecution.state || "").trim().toLowerCase()
          )
        );

        setIsRunning(isExecutionActuallyRunning);

        if (isExecutionActuallyRunning && activeExecution) {
          activeExecutionRef.current = activeExecution;
          const mappedCard = mapActiveExecutionToQueueTest(activeExecution);
          setActiveTest(mappedCard);
        } else {
          activeExecutionRef.current = null;
          setActiveTest(null);
        }
      }
    } catch (err) {
      console.error("Failed to fetch validation execution status:", err);
    }
  }, [missionId, truetwinId, mapActiveExecutionToQueueTest]);

  const fetchSessionExecutedTests = useCallback(async () => {
    if (!missionId || !scenarioId) return;

    try {
      const res: any = await ValidateService.getSessionResult(missionId, scenarioId);
      const raw = res?.data?.data ?? res?.data;
      if (!raw || typeof raw !== "object") return;

      const rawResults = raw.results ?? raw.Results;
      if (!Array.isArray(rawResults)) return;

      const mappedTests: QueueTest[] = rawResults.map((item: any) => {
        const rawStatus = item?.status ?? item?.Status ?? item?.state ?? item?.State;
        const status = normalizeQueueTestStatus(rawStatus);

        const testCaseName = String(item?.testCaseName ?? item?.TestCaseName ?? "").trim();
        const testPlanName = String(item?.testPlanName ?? item?.TestPlanName ?? "").trim();

        const testCaseId = String(
          item?.testCaseResultId ??
          item?.testCaseId ??
          item?.TestCaseResultId ??
          item?.TestCaseId ??
          ""
        ).trim();

        const testPlanId = String(
          item?.testPlanResultId ??
          item?.testPlanId ??
          item?.TestPlanResultId ??
          item?.TestPlanId ??
          ""
        ).trim();

        const rawResultType = String(
          item?.resultType ?? item?.ResultType ?? item?.type ?? ""
        ).trim().toLowerCase();
        const isExplicitTestCase = rawResultType === "testcase";
        const isExplicitTestPlan = rawResultType === "testplan";

        const isTestCase = isExplicitTestCase
          ? true
          : isExplicitTestPlan
          ? false
          : Boolean((testCaseName || testCaseId) && !testPlanName && !testPlanId);

        const testId = isTestCase
          ? (testCaseId || item?.id || uuidv4())
          : (testPlanId || item?.id || uuidv4());

        const name = isTestCase
          ? (testCaseName || item?.name || `Test Case ${testId}`)
          : (testPlanName || item?.name || `Test Plan ${testId}`);

        let passed: number;
        let failed: number;

        if (isTestCase) {
          // For executed Test Case: use "pass" and "fail" keys
          passed = Number(item?.pass ?? item?.Pass ?? 0);
          failed = Number(item?.fail ?? item?.Fail ?? 0);
        } else {
          // For executed Test Plan: use "testCasesPassed" and "testCasesFailed" keys
          passed = Number(
            item?.testCasesPassed ??
            item?.TestCasesPassed ??
            item?.testCasesPass ??
            item?.TestCasesPass ??
            0
          );
          failed = Number(
            item?.testCasesFailed ??
            item?.TestCasesFailed ??
            item?.testCasesFail ??
            item?.TestCasesFail ??
            0
          );
        }

        let total: number;
        if (isTestCase) {
          total = Number(
            item?.total ??
            item?.Total ??
            item?.totalEntries ??
            item?.TotalEntries ??
            0
          );
        } else {
          total = Number(
            item?.totalTestCases ??
            item?.TotalTestCases ??
            item?.total ??
            item?.Total ??
            0
          );
        }

        if (total === 0 && (passed > 0 || failed > 0)) {
          total = passed + failed;
        }

        if (total === 0) {
          if (isTestCase) {
            total = 1;
            if (status === "passed") {
              passed = 1;
              failed = 0;
            } else if (status === "failed") {
              passed = 0;
              failed = 1;
            }
          }
        }

        const completed = isTestCase
          ? Number(
              item?.completed ??
              item?.Completed ??
              item?.completedEntries ??
              (passed + failed)
            )
          : Number(
              item?.resolvedTestCases ??
              item?.ResolvedTestCases ??
              item?.completedTestCases ??
              item?.CompletedTestCases ??
              item?.completed ??
              item?.Completed ??
              (passed + failed)
            );

        let duration = "0 sec";
        const rawDuration = item?.duration ?? item?.Duration ?? item?.durationMinutes ?? item?.DurationMinutes;
        if (typeof rawDuration === "string" && rawDuration.trim()) {
          duration = rawDuration.trim();
        } else if (typeof rawDuration === "number" && !isNaN(rawDuration) && rawDuration > 0) {
          const ms = rawDuration > 10000 ? rawDuration : rawDuration * 1000;
          duration = formatRemainingTimeMS(ms);
        } else if (
          typeof item?.startTime === "number" &&
          typeof item?.endTime === "number" &&
          item.endTime > item.startTime
        ) {
          duration = formatRemainingTimeMS(item.endTime - item.startTime);
        }

        const percentComplete =
          item?.percentComplete !== undefined
            ? Number(item.percentComplete)
            : (total > 0 ? Math.round((completed / total) * 100) : 0);
        const remainingPercent =
          item?.remainingPercent !== undefined
            ? Number(item.remainingPercent)
            : Math.max(0, 100 - percentComplete);

        return {
          id: testId,
          name,
          duration,
          completed,
          total,
          passed,
          failed,
          testCasesPassed: item?.testCasesPassed !== undefined ? Number(item.testCasesPassed) : undefined,
          testCasesFailed: item?.testCasesFailed !== undefined ? Number(item.testCasesFailed) : undefined,
          totalTestCases:
            item?.totalTestCases !== undefined || item?.TotalTestCases !== undefined
              ? Number(item?.totalTestCases ?? item?.TotalTestCases)
              : undefined,
          percentComplete,
          remainingPercent,
          status,
          rawStatus: typeof rawStatus === "string" ? rawStatus : undefined,
          statusLabel: rawStatus ? formatQueueStatusLabel(rawStatus) : undefined,
          testType: isTestCase ? "Test Case" : "Test Plan",
          testCaseId: testCaseId || undefined,
          testPlanId: testPlanId || undefined,
          testCaseName: testCaseName || undefined,
          testPlanName: testPlanName || undefined,
          testCaseResultId: String(item?.testCaseResultId ?? item?.TestCaseResultId ?? "").trim() || undefined,
          testPlanResultId: String(item?.testPlanResultId ?? item?.TestPlanResultId ?? "").trim() || undefined,
        };
      });

      const completedTests = mappedTests.filter((test) => test.status !== "running");
      setExecutedTests(completedTests);
    } catch (err) {
      console.error("Failed to fetch session executed tests:", err);
    }
  }, [missionId, scenarioId]);

  const handleWebSocketMessage = useCallback((msg: any) => {
    if (!msg) return;

    let eventType = msg.message || msg.eventType || msg.type;
    let body = msg.data || msg.body || msg;

    // Handle stringified inner JSON if message is a JSON string
    if (typeof eventType === "string" && eventType.startsWith("{")) {
      try {
        const parsed = JSON.parse(eventType);
        eventType = parsed.message || parsed.eventType || parsed.type || eventType;
        if (parsed.data || parsed.body) {
          body = parsed.data || parsed.body;
        }
      } catch {
        // ignore
      }
    }

    if (body?.truetwinId && truetwinId && body.truetwinId !== truetwinId) {
      return;
    }

    const isComplete = Boolean(
      eventType === WS_TEST_PLAN_EXECUTION_COMPLETE ||
      eventType === WS_TEST_CASE_EXECUTION_COMPLETE ||
      (typeof eventType === "string" &&
        (eventType.toUpperCase().includes("COMPLETE") ||
         eventType.toUpperCase().includes("FINISH")))
    );

    const isAbort = Boolean(
      eventType === WS_TEST_PLAN_EXECUTION_CANCELLED ||
      eventType === WS_TEST_CASE_EXECUTION_CANCELLED ||
      eventType === WS_TEST_PLAN_EXECUTION_ABORTED ||
      eventType === WS_TEST_CASE_EXECUTION_ABORTED ||
      eventType === WS_VALIDATE_ABORT_EVENT ||
      eventType === "VALIDATION_TEST_CASE_ABORTED" ||
      (typeof eventType === "string" &&
        (eventType.toUpperCase().includes("ABORT") || eventType.toUpperCase().includes("CANCEL")))
    );

    if (isComplete) {
      setIsRunning(false);
      setActiveTest(null);
      activeExecutionRef.current = null;
      fetchSessionExecutedTests();
      setTimeout(() => {
        fetchExecutionStatus();
      }, 800);
      return;
    }

    if (isAbort) {
      setIsRunning(false);
      setActiveTest(null);
      activeExecutionRef.current = null;
      setIsAborting(false);
      fetchSessionExecutedTests();
      setTimeout(() => {
        fetchExecutionStatus();
      }, 800);
      return;
    }

    const isProgressUpdate =
      !isComplete &&
      !isAbort &&
      (eventType === WS_TESTPLAN_PROGRESS_UPDATED ||
        eventType === WS_TESTCASE_PROGRESS_UPDATED ||
        (body?.totalTestCases !== undefined && body?.resolvedTestCases !== undefined) ||
        (body?.testPlanResultId && body?.resolvedTestCases !== undefined) ||
        (body?.passed !== undefined && body?.failed !== undefined && (body?.testPlanResultId || body?.testCaseResultId || body?.currentTestCaseId)));

    if (
      eventType === WS_TEST_PLAN_EXECUTION_IN_PROGRESS ||
      eventType === WS_TEST_CASE_EXECUTION_IN_PROGRESS
    ) {
      setIsRunning(true);
      const isPlan = eventType === WS_TEST_PLAN_EXECUTION_IN_PROGRESS;
      const total = isPlan
        ? Number(body.totalTestCases ?? 0)
        : Number(body.totalEntries ?? 0);
      const completed = isPlan
        ? Number(body.resolvedTestCases ?? 0)
        : Number(body.completedEntries ?? 0);

      const { passed, failed } = extractPassFailCounts(body);

      const durationStr = body.remainingTimeMS !== undefined
        ? formatRemainingTimeMS(body.remainingTimeMS)
        : (body.estimatedDurationMS
          ? formatRemainingTimeMS(body.estimatedDurationMS)
          : "0 min");

      const percentComplete =
        body.percentComplete !== undefined
          ? Number(body.percentComplete)
          : (total > 0 ? Math.round((completed / total) * 100) : 0);
      const remainingPercent =
        body.remainingPercent !== undefined
          ? Number(body.remainingPercent)
          : Math.max(0, 100 - percentComplete);

      setActiveTest((prev) => {
        const currentRef = activeExecutionRef.current;
        const testPlanId = isPlan
          ? (body.testPlanId || prev?.testPlanId || currentRef?.testPlanId || getStoredTestId("TEST PLAN"))
          : undefined;
        const testCaseId = !isPlan
          ? (body.testCaseId || prev?.testCaseId || currentRef?.testCaseId || getStoredTestId("TEST"))
          : undefined;
        const testPlanResultId = isPlan
          ? (body.testPlanResultId || prev?.testPlanResultId || currentRef?.testPlanResultId)
          : undefined;
        const testCaseResultId = !isPlan
          ? (body.testCaseResultId || prev?.testCaseResultId || currentRef?.testCaseResultId)
          : undefined;

        const testId = isPlan
          ? (testPlanId || testPlanResultId || prev?.id || "active-plan")
          : (testCaseId || testCaseResultId || prev?.id || "active-test");

        const resolvedName = isPlan
          ? (body.testPlanName && !isGenericActiveName(body.testPlanName) ? body.testPlanName : "") ||
            (prev?.testPlanName && !isGenericActiveName(prev.testPlanName) ? prev.testPlanName : "") ||
            (prev?.name && !isGenericActiveName(prev.name) ? prev.name : "") ||
            (activeExecutionRef.current?.testPlanName && !isGenericActiveName(activeExecutionRef.current.testPlanName) ? activeExecutionRef.current.testPlanName : "") ||
            getStoredTestName("TEST PLAN", testPlanId) ||
            (testPlanId ? `Test Plan ${testPlanId}` : "Test Plan")
          : (body.testCaseName && !isGenericActiveName(body.testCaseName) ? body.testCaseName : "") ||
            (prev?.testCaseName && !isGenericActiveName(prev.testCaseName) ? prev.testCaseName : "") ||
            (prev?.name && !isGenericActiveName(prev.name) ? prev.name : "") ||
            (body.currentTestCaseName && !isGenericActiveName(body.currentTestCaseName) ? body.currentTestCaseName : "") ||
            (activeExecutionRef.current?.testCaseName && !isGenericActiveName(activeExecutionRef.current.testCaseName) ? activeExecutionRef.current.testCaseName : "") ||
            getStoredTestName("TEST", testCaseId) ||
            (testCaseId ? `Test Case ${testCaseId}` : "Test Case");

        activeExecutionRef.current = {
          kind: isPlan ? "TEST_PLAN" : "TEST_CASE",
          testPlanId,
          testPlanResultId,
          testPlanName: isPlan ? resolvedName : undefined,
          testCaseId: !isPlan ? testCaseId : body.currentTestCaseId,
          testCaseResultId: !isPlan ? testCaseResultId : undefined,
          testCaseName: !isPlan ? resolvedName : undefined,
          currentTestCaseId: body.currentTestCaseId,
          currentTestCaseName: body.currentTestCaseName,
          estimatedDurationMS: body.estimatedDurationMS,
          remainingTimeMS: body.remainingTimeMS,
          percentComplete,
          remainingPercent,
          passed,
          failed,
        };

        return {
          id: testId,
          name: resolvedName,
          duration: durationStr,
          completed,
          total,
          passed,
          failed,
          percentComplete,
          remainingPercent,
          status: "running",
          testType: isPlan ? "Test Plan" : "Test Case",
          testPlanId,
          testPlanResultId,
          testCaseId,
          testCaseResultId,
          testPlanName: isPlan ? resolvedName : undefined,
          testCaseName: !isPlan ? resolvedName : undefined,
          currentTestCaseName: body.currentTestCaseName,
        };
      });
    } else if (isProgressUpdate) {
      setIsRunning(true);
      const isExplicitTestCase =
        eventType === WS_TESTCASE_PROGRESS_UPDATED ||
        Boolean(body?.testCaseResultId && !body?.testPlanResultId) ||
        Boolean(body?.totalEntries !== undefined && body?.totalTestCases === undefined);

      const isExplicitTestPlan =
        eventType === WS_TESTPLAN_PROGRESS_UPDATED ||
        body?.totalTestCases !== undefined ||
        Boolean(body?.testPlanResultId && !body?.testCaseResultId);

      const isPlan =
        isExplicitTestPlan ||
        (!isExplicitTestCase && activeExecutionRef.current?.kind === "TEST_PLAN");

      const incomingId = isPlan
        ? (body.testPlanId || body.testPlanResultId)
        : (body.testCaseId || body.testCaseResultId);

      setActiveTest((prev) => {
        const isPlanMismatch = Boolean(
          prev && ((isPlan && prev.testType !== "Test Plan") || (!isPlan && prev.testType === "Test Plan"))
        );
        const hasExplicitIdConflict = Boolean(
          prev &&
          ((isPlan && body.testPlanId && prev.testPlanId && String(body.testPlanId).trim().toLowerCase() !== String(prev.testPlanId).trim().toLowerCase()) ||
           (!isPlan && body.testCaseId && prev.testCaseId && String(body.testCaseId).trim().toLowerCase() !== String(prev.testCaseId).trim().toLowerCase()))
        );
        const isDifferentTest = !prev || isPlanMismatch || hasExplicitIdConflict;

        const total = isPlan
          ? Number(body.totalTestCases ?? (isDifferentTest ? 0 : (prev?.total ?? 0)))
          : Number(body.totalEntries ?? (isDifferentTest ? 0 : (prev?.total ?? 0)));
        const completed = isPlan
          ? Number(body.resolvedTestCases ?? (isDifferentTest ? 0 : (prev?.completed ?? 0)))
          : Number(body.completedEntries ?? (isDifferentTest ? 0 : (prev?.completed ?? 0)));

        const { passed, failed } = extractPassFailCounts(
          body,
          isDifferentTest ? 0 : prev?.passed,
          isDifferentTest ? 0 : prev?.failed,
        );

        const { duration: totalDurationStr, remainingDuration } =
          calculateProgressDurations(
            body,
            isDifferentTest ? undefined : prev?.duration,
            total,
            completed,
          );

        const currentRef = activeExecutionRef.current;
        const testPlanId = isPlan
          ? (body.testPlanId || prev?.testPlanId || currentRef?.testPlanId || getStoredTestId("TEST PLAN"))
          : undefined;
        const testCaseId = !isPlan
          ? (body.testCaseId || prev?.testCaseId || currentRef?.testCaseId || getStoredTestId("TEST"))
          : undefined;
        const testPlanResultId = isPlan
          ? (body.testPlanResultId || prev?.testPlanResultId || currentRef?.testPlanResultId)
          : undefined;
        const testCaseResultId = !isPlan
          ? (body.testCaseResultId || prev?.testCaseResultId || currentRef?.testCaseResultId)
          : undefined;

        // Determine test plan name vs current test case name
        let resolvedName: string;
        if (isPlan) {
          resolvedName =
            (body.testPlanName && !isGenericActiveName(body.testPlanName) ? body.testPlanName : "") ||
            (prev?.testPlanName && !isGenericActiveName(prev.testPlanName) ? prev.testPlanName : "") ||
            (prev?.name && !isGenericActiveName(prev.name) ? prev.name : "") ||
            (currentRef?.testPlanName && !isGenericActiveName(currentRef.testPlanName) ? currentRef.testPlanName : "") ||
            getStoredTestName("TEST PLAN", testPlanId) ||
            (testPlanId ? `Test Plan ${testPlanId}` : "") ||
            (prev?.name && !isGenericActiveName(prev.name) ? prev.name : "") ||
            "Test Plan";
        } else {
          resolvedName =
            (body.testCaseName && !isGenericActiveName(body.testCaseName) ? body.testCaseName : "") ||
            (prev?.testCaseName && !isGenericActiveName(prev.testCaseName) ? prev.testCaseName : "") ||
            (prev?.name && !isGenericActiveName(prev.name) ? prev.name : "") ||
            (body.currentTestCaseName && !isGenericActiveName(body.currentTestCaseName) ? body.currentTestCaseName : "") ||
            (currentRef?.testCaseName && !isGenericActiveName(currentRef.testCaseName) ? currentRef.testCaseName : "") ||
            getStoredTestName("TEST", testCaseId) ||
            (testCaseId ? `Test Case ${testCaseId}` : "") ||
            (prev?.name && !isGenericActiveName(prev.name) ? prev.name : "") ||
            "Test Case";
        }

        const testId =
          (isPlan ? testPlanId : testCaseId) ||
          incomingId ||
          (!isDifferentTest ? prev?.id : null) ||
          (isPlan ? "active-plan" : "active-test");

        const percentComplete =
          body?.percentComplete !== undefined
            ? Number(body.percentComplete)
            : (total > 0 ? Math.round((completed / total) * 100) : (isDifferentTest ? 0 : (prev?.percentComplete ?? 0)));

        const remainingPercent =
          body?.remainingPercent !== undefined
            ? Number(body.remainingPercent)
            : Math.max(0, 100 - percentComplete);

        activeExecutionRef.current = {
          kind: isPlan ? "TEST_PLAN" : "TEST_CASE",
          testPlanId,
          testPlanResultId,
          testPlanName: isPlan ? resolvedName : undefined,
          testCaseId: !isPlan ? testCaseId : (body.currentTestCaseId || currentRef?.testCaseId),
          testCaseResultId: !isPlan ? testCaseResultId : currentRef?.testCaseResultId,
          testCaseName: !isPlan ? resolvedName : undefined,
          currentTestCaseName: body.currentTestCaseName,
          totalTestCases: isPlan ? total : undefined,
          resolvedTestCases: isPlan ? completed : undefined,
          totalEntries: !isPlan ? total : undefined,
          completedEntries: !isPlan ? completed : undefined,
          remainingTimeMS: body.remainingTimeMS,
          percentComplete,
          remainingPercent,
          passed,
          failed,
        };

        // Trigger state refresh when execution reaches 100% complete
        if (percentComplete >= 100 || (total > 0 && completed >= total)) {
          setIsRunning(false);
          setTimeout(() => {
            fetchSessionExecutedTests();
            fetchExecutionStatus();
          }, 500);
        }

        return {
          id: testId,
          name: resolvedName,
          duration: totalDurationStr,
          remainingDuration,
          completed,
          total,
          passed,
          failed,
          percentComplete,
          remainingPercent,
          status: "running",
          testType: isPlan ? "Test Plan" : "Test Case",
          currentTestCaseName: body.currentTestCaseName,
          testPlanId,
          testPlanResultId,
          testCaseId,
          testCaseResultId,
          testPlanName: isPlan ? resolvedName : undefined,
          testCaseName: !isPlan ? resolvedName : undefined,
        };
      });
    } else if (
      eventType === "VALIDATION_RESULT_UPDATED" ||
      eventType === "TESTCASE_RESULT_UPDATED" ||
      eventType === "TESTPLAN_RESULT_UPDATED" ||
      eventType === "SUBSYSTEM_HEALTH_UPDATED"
    ) {
      fetchExecutionStatus();
      fetchSessionExecutedTests();
    }
  }, [truetwinId, fetchExecutionStatus, fetchSessionExecutedTests]);

  const abortActiveExecution = useCallback(
    async (targetTest?: QueueTest | null) => {
      const target = targetTest || activeTest;
      if (!target || !missionId || !scenarioId || !truetwinId) {
        showToastMessage("error", "Cannot abort: Missing required session details");
        return;
      }

      setIsAborting(true);
      try {
        const info = activeExecutionRef.current;
        const isPlan =
          info?.kind === "TEST_PLAN" ||
          target.testType === "Test Plan" ||
          String(target.id).startsWith("TP");

        let res: any;
        if (isPlan) {
          // In case of test plan execution, strictly use Test Plan ID (definition ID), NEVER the Result ID
          let planTargetId =
            (target.testPlanId && target.testPlanId !== target.testPlanResultId ? target.testPlanId : "") ||
            (info?.testPlanId && info?.testPlanId !== info?.testPlanResultId ? info.testPlanId : "");

          // If not found in memory, query executionStatus API as source of truth
          if (!planTargetId) {
            try {
              const statusRes: any = await ValidateService.getValidationExecutionStatus(missionId, truetwinId);
              const activeExec: ActiveExecutionInfo | undefined =
                statusRes?.data?.data?.activeExecution || statusRes?.data?.activeExecution;
              if (activeExec?.testPlanId) {
                planTargetId = activeExec.testPlanId;
                activeExecutionRef.current = activeExec;
              }
            } catch (statusErr) {
              console.warn("Failed to fetch testPlanId from execution status API:", statusErr);
            }
          }

          // Fallback to stored testPlanId if API didn't return it
          if (!planTargetId) {
            planTargetId =
              sessionStorage.getItem("testPlanId") ||
              getStoredTestId("TEST PLAN") ||
              (target.id && target.id !== target.testPlanResultId && target.id !== info?.testPlanResultId
                ? String(target.id)
                : "");
          }

          if (!planTargetId) {
            showToastMessage("error", "Cannot abort: Test Plan ID not found");
            return;
          }

          res = await ValidateService.cancelTestPlan(
            missionId,
            planTargetId,
            scenarioId,
            truetwinId,
          );
        } else {
          // In case of test case execution, strictly use Test Case ID (definition ID), NEVER the Result ID
          let caseTargetId =
            (target.testCaseId && target.testCaseId !== target.testCaseResultId ? target.testCaseId : "") ||
            (info?.testCaseId && info?.testCaseId !== info?.testCaseResultId ? info.testCaseId : "");

          // If not found in memory, query executionStatus API as source of truth
          if (!caseTargetId) {
            try {
              const statusRes: any = await ValidateService.getValidationExecutionStatus(missionId, truetwinId);
              const activeExec: ActiveExecutionInfo | undefined =
                statusRes?.data?.data?.activeExecution || statusRes?.data?.activeExecution;
              if (activeExec?.testCaseId) {
                caseTargetId = activeExec.testCaseId;
                activeExecutionRef.current = activeExec;
              }
            } catch (statusErr) {
              console.warn("Failed to fetch testCaseId from execution status API:", statusErr);
            }
          }

          // Fallback to stored testCaseId if API didn't return it
          if (!caseTargetId) {
            caseTargetId =
              sessionStorage.getItem("testCaseId") ||
              getStoredTestId("TEST") ||
              (target.id && target.id !== target.testCaseResultId && target.id !== info?.testCaseResultId
                ? String(target.id)
                : "");
          }

          if (!caseTargetId) {
            showToastMessage("error", "Cannot abort: Test Case ID not found");
            return;
          }

          res = await ValidateService.cancelTestCase(
            missionId,
            caseTargetId,
            scenarioId,
            truetwinId,
          );
        }

        const toastMsg =
          res?.data?.message ||
          res?.message ||
          (isPlan
            ? `Test plan cancelled successfully`
            : `Test case cancelled successfully`);

        showToastMessage("success", toastMsg);
        setActiveTest((prev) =>
          prev
            ? {
                ...prev,
                status: "cancelled",
                rawStatus: "MANUAL_ABORT",
                statusLabel: "Manual Abort",
              }
            : null,
        );
        fetchExecutionStatus();
        fetchSessionExecutedTests();
      } catch (err: any) {
        const errorMsg =
          err?.response?.data?.message ||
          err?.data?.message ||
          err?.message;

        const statusCode = err?.response?.status || err?.status;
        if (statusCode === 404) {
          showToastMessage("info", errorMsg || "No active execution found for this test");
          fetchExecutionStatus();
          fetchSessionExecutedTests();
        } else if (statusCode === 409) {
          showToastMessage("info", errorMsg || "Execution has already finished");
          fetchExecutionStatus();
          fetchSessionExecutedTests();
        } else {
          showToastMessage("error", errorMsg || "Failed to cancel test execution");
        }
      } finally {
        setIsAborting(false);
      }
    },
    [activeTest, missionId, scenarioId, truetwinId, fetchSessionExecutedTests, fetchExecutionStatus],
  );

  useEffect(() => {
    fetchExecutionStatus();
    fetchSessionExecutedTests();
  }, [fetchExecutionStatus, fetchSessionExecutedTests]);

  useEffect(() => {
    const handleWsEvent = (e: any) => {
      if (e.detail) {
        handleWebSocketMessage(e.detail);
      }
    };

    const handleStatusRefetch = () => {
      fetchExecutionStatus();
      fetchSessionExecutedTests();
    };

    window.addEventListener("validation_ws_message", handleWsEvent);
    window.addEventListener("validation_status_refetch", handleStatusRefetch);
    return () => {
      window.removeEventListener("validation_ws_message", handleWsEvent);
      window.removeEventListener("validation_status_refetch", handleStatusRefetch);
    };
  }, [handleWebSocketMessage, fetchExecutionStatus, fetchSessionExecutedTests]);

  useEffect(() => {
    if (!socket) return;

    const handleSocketMessage = (event: MessageEvent) => {
      try {
        if (!event.data) return;
        const msg = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        handleWebSocketMessage(msg);
      } catch (err) {
        // Silently skip non-JSON or raw binary frames
      }
    };

    socket.addEventListener("message", handleSocketMessage);
    return () => {
      socket.removeEventListener("message", handleSocketMessage);
    };
  }, [socket, handleWebSocketMessage]);

  const visibleExecutedTests = useMemo(
    () =>
      filterVisibleExecutedTests(
        executedTests,
        activeTest,
        activeExecutionRef.current,
      ),
    [executedTests, activeTest],
  );

  return {
    activeTest,
    executedTests: visibleExecutedTests,
    isRunning,
    isAborting,
    abortActiveExecution,
    fetchExecutionStatus,
    fetchSessionExecutedTests,
    handleWebSocketMessage,
  };
};

export default useValidationExecution;
